import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { CircleCheck, Crown, Gift, Lock, X } from "lucide-react";
import { useClaimReward } from "../../../../queries/professoru/forum.query";
import { PricingPlanModal } from "../../../../components/PricingPlanModal";

const formatUnlockText = (text) => {
  if (!text) return text;
  return text.replace(
    /(\d+)\s*AI\s*credits\s*\(([^)]*)/gi,
    (match, num, rest) => {
      const trimmed = rest.trim();
      return `${num} AI credits (${trimmed.charAt(0).toUpperCase() + trimmed.slice(1)}`;
    }
  );
};

export default function RewardModal({
  isOpen,
  onClose,
  data,
  onCTA,
}) {
  const { mutate: claimReward, isPending: isClaiming } = useClaimReward();
  const { hasActiveSubscription } = useSelector((state) => state.billing || {});
  const [hasClaimed, setHasClaimed] = useState(Boolean(data?.isClaimed));
  const [showPricingPlanModal, setShowPricingPlanModal] = useState(false);

  useEffect(() => {
    setHasClaimed(Boolean(data?.isClaimed));
  }, [data?.id, data?.isClaimed]);

  if (!isOpen) return null;

  const isCompleted = Boolean(data?.isCompleted);
  const isClaimed = Boolean(data?.isClaimed) || hasClaimed;
  const isLocked = Boolean(data?.isLocked) && !data?.isUnlocked && !isCompleted;
  const isDisabled = isLocked || isCompleted;
  const showGoProButton = isCompleted && !isClaimed && !hasActiveSubscription;
  const showClaimButton = isCompleted && !isClaimed && hasActiveSubscription;

  const handleClaimReward = (e) => {
    e.stopPropagation();
    if (!data?.id || isClaiming) return;
    claimReward(data.id, {
      onSuccess: () => setHasClaimed(true),
    });
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3"
    //   onClick={onClose}
    >
      
      {/* Modal */}
      <div 
        className="w-full max-w-md bg-theme_clr1 rounded-2xl p-5 sm:p-6 relative text-white"
        onClick={(e) => e.stopPropagation()}
      >

        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3"
        >
          <X />
        </button>

        {/* Header */}
        <h2 className="text-lg sm:text-xl font-semibold mb-4">
          🎁 {data.title}
        </h2>

        {/* Description */}
        <div className="mb-4">
          <p className="text-sm text-gray-200 leading-relaxed">
            {data?.description}
          </p>
        </div>

        {/* Divider */}
        <div className="h-[1px] bg-white/20 my-4" />

        {/* Unlock Section */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold mb-2">
            How to unlock?
          </h3>

          <p className="text-sm text-gray-300 leading-relaxed">
            {data?.unlock}
          </p>
        </div>

        <div className="mb-6">
          <h3 className="text-sm font-semibold mb-2">
            Unlocks
          </h3>

          <p className="text-sm text-gray-300 leading-relaxed">
            {formatUnlockText(data?.unlockReward)}
          </p>
        </div>

        {/* CTA */}
        <div className="group relative w-full space-y-3">
          <button
            onClick={onCTA}
            disabled={isDisabled}
            className={`w-full py-2 rounded-lg font-medium transition inline-flex items-center justify-center gap-2 ${
              isCompleted
                ? "bg-green-500/20 text-green-400 cursor-not-allowed"
                : isLocked
                  ? "bg-gray-500/40 text-gray-300 cursor-not-allowed"
                  : "bg-theme_clr2 hover:bg-orange-600 text-white"
            }`}
          >
            {isClaimed ? (
              <>
                <CircleCheck className="w-5 h-5" aria-hidden="true" />
                <span className="underline">Reward is claimed</span>
              </>
            ) : isCompleted ? (
              <>
                <CircleCheck className="w-5 h-5" aria-hidden="true" />
                <span className="underline">Completed</span>
              </>
            ) : (
              <>
                {isLocked && <Lock className="w-4 h-4" aria-hidden="true" />}
                {data?.ctaText || "Unlock Now"}
              </>
            )}
          </button>
          {showGoProButton && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowPricingPlanModal(true);
              }}
              className="w-full py-2 rounded-lg font-medium transition inline-flex items-center justify-center gap-2 bg-theme_clr2 hover:bg-orange-600 text-white"
            >
              <Crown className="w-5 h-5" aria-hidden="true" />
              <span>Go Pro to Claim Reward</span>
            </button>
          )}
          {showClaimButton && (
            <button
              type="button"
              onClick={handleClaimReward}
              disabled={isClaiming}
              className="w-full py-2 rounded-lg font-medium transition inline-flex items-center justify-center gap-2 bg-theme_clr2 hover:bg-orange-600 text-white disabled:opacity-70 disabled:cursor-not-allowed"
            >
              <Gift className="w-5 h-5" aria-hidden="true" />
              <span>{isClaiming ? "Claiming..." : "Claim Reward"}</span>
            </button>
          )}
          {isLocked && (
            <span
              role="tooltip"
              className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 w-max max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded bg-gray-900 px-3 py-2 text-center text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100"
            >
              This reward is locked, To Unlock this reward complete the previous Rewards
            </span>
          )}
        </div>
      </div>
      <PricingPlanModal
        isOpen={showPricingPlanModal}
        onClose={() => setShowPricingPlanModal(false)}
        returnUrl={window.location.origin + "/admin/professor-u/rewards"}
      />
    </div>
  );
}